import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { Clock, BookOpen, Users, CheckCircle } from 'lucide-react';

const fetchCourse = async (id) => {
  const res = await axios.get(`/courses/${id}`);
  return res.data;
};

const fetchLessons = async (id) => {
  const res = await axios.get(`/lessons/course/${id}`);
  return res.data;
};

export default function CourseDetailPage() {
  const { id } = useParams();
  
  const { data: courseData, isLoading: courseLoading } = useQuery(
    ['course', id],
    () => fetchCourse(id)
  );
  const { data: lessonsData, isLoading: lessonsLoading } = useQuery(
    ['lessons', id],
    () => fetchLessons(id)
  );

  const course = courseData?.data;
  const lessons = lessonsData?.data || [];

  if (courseLoading) return <div className="text-center py-12">กำลังโหลด...</div>;
  if (!course) return <div className="text-center py-12">ไม่พบคอร์ส</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-8 mb-8">
        <div className="max-w-3xl">
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">{course.category_name}</span>
          <h1 className="text-4xl font-bold mt-4 mb-4">{course.title}</h1>
          <p className="text-blue-100 mb-6">{course.description}</p>
          <div className="flex items-center gap-6 text-sm">
            <span className="flex items-center gap-1"><Clock size={16} /> {course.duration} ชม.</span>
            <span className="flex items-center gap-1"><BookOpen size={16} /> {lessons.length} บทเรียน</span>
            <span className="flex items-center gap-1"><Users size={16} /> {course.enrollment_count} ผู้ลงทะเบียน</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Course Content */}
        <div className="lg:col-span-2">
          <h2 className="text-2xl font-bold mb-4">เนื้อหาคอร์ส</h2>
          <div className="space-y-2">
            {lessonsLoading ? (
              <p>กำลังโหลด...</p>
            ) : lessons.length === 0 ? (
              <p className="text-gray-500">ยังไม่มีบทเรียน</p>
            ) : (
              lessons.map((lesson, idx) => (
                <div key={lesson._id} className="flex items-center gap-4 p-4 bg-white rounded-lg border hover:border-blue-300 transition">
                  <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-bold text-gray-600">
                    {idx + 1}
                  </span>
                  <div className="flex-grow">
                    <h3 className="font-medium">{lesson.title}</h3>
                    <p className="text-sm text-gray-500">{lesson.contentType} · {lesson.videoDuration || 0} นาที</p>
                  </div>
                  {lesson.isFree && <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">ฟรี</span>}
                  <CheckCircle size={20} className="text-gray-300" />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Sidebar */}
        <div>
          <div className="bg-white rounded-xl shadow-sm p-6 border sticky top-8">
            <div className="text-3xl font-bold text-blue-600 mb-4">
              {course.price === 0 ? 'ฟรี' : `฿${course.price}`}
            </div>
            <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition mb-3">
              ลงทะเบียนเรียน
            </button>
            <button className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition">
              ใส่รายการโปรด
            </button>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-2"><CheckCircle size={16} className="text-green-500" /> เรียนได้ตลอดชีพ</div>
              <div className="flex items-center gap-2"><CheckCircle size={16} className="text-green-500" /> ใบรับรองจบคอร์ส</div>
              <div className="flex items-center gap-2"><CheckCircle size={16} className="text-green-500" /> แบบทดสอบประเมินผล</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
